import type { Nullable, UUID } from '../../shared/lib/typeHelpers.no-deps'
import type { Request as HttpRequest } from '../http/types.no-deps'
import type { PointsAwardedEvent } from '../../shared/domain/gamification/types.no-deps'
import type { FileId } from '../../shared/domain/files/types.no-deps'
import type { RichTextParagraph } from '../../shared/domain/richText/types.no-deps'
import type { OngoingReportIn } from '../../shared/domain/reports/ongoingReport.no-deps'
import type { ReportClientId } from '../../shared/domain/reports/types.no-deps'
import type { ClientLog, ClientLogMetadata } from '../../shared/lib/ClientLog.no-deps'
import type { Flow, Answer as FlowAnswer } from '../../shared/domain/flows/types.no-deps'
import { getAllRequest as getTags } from '../http/tags/getAll.no-deps'
import { type AddEndpoint as AddLogsEndpoint, addRequest as addLogsRequest } from '../http/logs/add.no-deps'
import { type EditEndpoint as EditActionEndpoint, editRequest as editActionRequest } from '../http/actions/edit.no-deps'
import { type GenerateUploadUrlEndpoint, generateUploadUrlRequest } from '../http/files/generateUploadUrl.no-deps'
import { type Add as AddFileMetaEndpoint, add as addFileMetaRequest } from '../http/files/addMeta.no-deps'
import {
    type StartMultipartUploadEndpoint,
    startMultipartUploadRequest,
} from '../http/files/startMultipartUpload.no-deps'
import { type AwardPointsEndpoint, awardPointsRequest } from '../http/gamification/awardPoint.no-deps'
import { type ConfirmActionEndpoint, confirmActionRequest } from '../http/reports/confirmAction.no-deps'
import { type SyncDoneEndpoint, syncDoneRequest } from '../http/reports/syncDone.no-deps'
import { type Get as GetFileMetaEndpoint, get as getFileMetaRequest } from '../http/files/getMeta.no-deps'
import { type GetFileEndpoint, getFileRequest } from '../http/files/getFile.no-deps'
import { type GetMetaBulkEndpoint, getMetaBulk as getMetaBulkRequest } from '../http/files/getMetaBulk.no-deps'
import { type RemoveCommentEndpoint, removeCommentRequest } from '../http/posts/removeComment.no-deps'
import { type EditCommentEndpoint, editCommentRequest } from '../http/posts/editComment.no-deps'
import {
    type AddEndpoint as AddScheduleEndpoint,
    addRequest as addScheduleRequest,
} from '../http/schedules/add.no-deps'
import {
    type GetEndpoint as GetScheduleEndpoint,
    getRequest as getScheduleRequest,
} from '../http/schedules/get.no-deps'
import { type SyncTodoEndpoint, syncTodoRequest } from '../http/schedules/syncTodo.no-deps'
import {
    type SyncNotificationsEndpoint,
    syncNotificationsRequest,
} from '../http/notifications/syncNotifications.no-deps'
import { type SyncTeamsEndpoint, syncTeamsRequest } from '../http/teams/syncTeams.no-deps'
import { getAll as syncReportStatusesRequest } from '../http/reportStatuses/getAll.no-deps'
import { getAll as syncReportStatusFlowsRequest } from '../http/reportStatusFlow/getAll.no-deps'
import { getUserReports } from '../http/reports/getUserReports.no-deps'
import {
    type GetEndpoint as GetWorkflowEndpoint,
    getRequest as getWorkflowRequest,
} from '../http/workflows/get.no-deps'
import {
    type AddEndpoint as AddWorkflowEndpoint,
    addRequest as addWorkflowRequest,
} from '../http/workflows/add.no-deps'
import {
    type GetEndpoint as GetProcedureEndpoint,
    getRequest as getProcedureRequest,
} from '../http/procedures/get.no-deps'
import {
    type GetAllProceduresEndpoint,
    getAllRequest as getAllProceduresRequest,
} from '../http/procedures/getAll.no-deps'
import {
    type WorkflowsEndpoint as GetWorkflowsEndpoint,
    workflowsRequest as getWorkflowsRequest,
} from '../http/procedures/workflows.no-deps'
import { type PingEndpoint, pingRequest } from '../http/healthCheck.no-deps'
import { type StartReportEndpoint, startReportRequest } from '../http/reports/start.no-deps'
import {
    getHashRequest as getReportHashReqeust,
    type GetHashEndpoint as GetReportHashEndpoint,
} from '../http/reports/getHash.no-deps'
import { createAnswersRequest, type CreateAnswersEndpoint } from '../http/reports/CreateAnswers.no-deps'
import { getAllWithNamesRequest } from '../http/unitTypes/getAllWithNames.no-deps'
import { createAiChatRequest } from '../http/aiChats/create.no-deps'
import type { ChatId, ChatPromptId } from '../../shared/domain/aiChats/types.no-deps'
import {
    type UpdateAiPromptFeedbackEndpoint,
    updateAiPromptFeedbackRequest,
} from '../http/aiChats/updatePromptFeedback.no-deps'
import { getReportsRequest } from '../http/units/getReports.no-deps'
import { getUnitReportFiltersRequest } from '../http/units/getReportFilters.no-deps'
import { searchRequest as searchUnitsRequest } from '../http/units/search.no-deps'
import {
    updateRequest as updateUnitsRequest,
    type UpdateEndpoint as UpdateUnitsEndpoint,
} from '../http/units/update.no-deps'

import {
    type CreateEndpoint as CreateProcedureEndpoint,
    createRequest as createProcedureRequest,
} from '../http/procedures/create.no-deps'

import {
    type DeleteEndpoint as DeleteActionEndpoint,
    deleteRequest as deleteActionRequest,
} from '../http/actions/delete.no-deps'

import {
    type CompleteMultipartUploadEndpoint,
    completeMultipartUploadRequest,
} from '../http/files/completeMultipartUpload.no-deps'

import {
    type GenerateUploadPartUrlEndpoint,
    generateUploadPartUrlRequest,
} from '../http/files/generateUploadPartUrl.no-deps'

import {
    type SyncEndpoint as SyncProceduresEndpoint,
    syncRequest as syncProceduresRequest,
} from '../http/procedures/sync.no-deps'

import {
    type GetAllEndpoint as GetAllUnitsEndpoint,
    getAllRequest as getAllUnitsRequest,
} from '../http/units/getAll.no-deps'

import {
    type GetAllWithNamesEndpoint as GetAllUnitsWithNamesEndpoint,
    getAllWithNamesRequest as getAllUnitsWithNamesRequest,
} from '../http/units/getAllWithNames.no-deps'

import {
    type GetManyWithNamesEndpoint as GetManyUnitsWithNamesEndpoint,
    getManyWithNamesRequest as getManyUnitsWithNamesRequest,
} from '../http/units/getManyWithNames.no-deps'

import {
    type GetAllWithLineageEndpoint as GetAllUnitsWithLineageEndpoint,
    getAllWithLineageRequest as getAllUnitsWithLineageRequest,
} from '../http/units/getAllWithLineage.no-deps'

import {
    type GetByBulkEditIdEndpoint as GetUnitsByBulkEditIdEndpoint,
    getByBulkEditIdRequest as getUnitsByBulkEditIdRequest,
} from '../http/units/getByBulkEditId.no-deps'

import { type GetEndpoint as GetUnitsEndpoint, get as getUnitsRequest } from '../http/units/get.no-deps'

import {
    type SyncEndpoint as SyncWorkflowsEndpoint,
    syncRequest as syncFlowWorkflowsRequest,
} from '../http/workflows/sync.no-deps'

import {
    type GetTimelineEndpoint as GetCommmentTimelineEndpoint,
    getTimeline as getCommentTimelineRequest,
} from '../http/posts/getTimeline.no-deps'

import {
    type RemoveEndpoint as RemoveReportEndpoint,
    removeRequest as removeReportRequest,
} from '../http/reports/remove.no-deps'

import {
    type MarkNotificationsAsReadEndpoint,
    markNotificationsAsReadRequest,
} from '../http/notifications/markNotificationsAsRead.no-deps'

import {
    type CreateEndpoint as CreateScheduleEndpoint,
    createRequest as createScheduleRequest,
} from '../http/schedules/create.no-deps'

import {
    type AddCollaboratorsEndpoint as AddReportCollaboratorsEndpoint,
    addCollaboratorsRequest as addReportCollaboratorsRequest,
} from '../http/reports/addCollaborators.no-deps'

import {
    type CancelEndpoint as CancelReportEndpoint,
    cancelRequest as cancelReportRequest,
} from '../http/reports/cancel.no-deps'

import {
    type GetCollaborativeEndpoint as GetCollaborativeReportsEndpoint,
    getCollaborativeRequest as getCollaborativeReportsRequest,
} from '../http/reports/getCollaborative.no-deps'

import {
    type GetOngoingEndpoint as GetOngoingReportEndpoint,
    getOngoingRequest as getOngoingReportRequest,
} from '../http/reports/getOngoing.no-deps'

import {
    type GetOngoingChangesEndpoint as GetOngoingReportChangesEndpoint,
    getOngoingChangesRequest as getOngoingReportChangesRequest,
} from '../http/reports/getOngoingChanges.no-deps'

import {
    finalizeRequest as finalizeReportRequest,
    type FinalizeEndpoint as FinalizeReportEndpoint,
} from '../http/reports/finalize.no-deps'

import {
    type GetTitleEndpoint as GetReportTitleEndpoint,
    getTitleRequest as getReportTitleRequest,
} from '../http/reports/getTitle.no-deps'

import {
    type EditEndpoint as EditWorkflowEndpoint,
    editRequest as editWorkflowRequest,
} from '../http/workflows/edit.no-deps'

import {
    type UnfinalizedEndpoint as GetUnfinalizedReportsEndpoint,
    unfinalizedRequest as getUnfinalizedReportsRequest,
} from '../http/reports/unfinalized.no-deps'

import {
    type GetByIdsEndpoint as GetUnitsByIdsEndpoint,
    getByIdsRequest as getUnitsByIdsRequest,
} from '../http/units/getByIds.no-deps'

import {
    type GetPhotoFilesEndpoint as GetReportPhotoFilesEndpoint,
    getPhotoFilesRequest as getReportPhotoFilesRequest,
} from '../http/reports/getPhotoFiles.no-deps'
import { type GetFilesS3urlEndpoint, getFilesS3UrlRequest } from '../http/files/getFilesS3Url.no-deps'

import {
    type GetAllEndpoint as GetAllGuidesEndpoint,
    getAllRequest as getAllGuidesRequest,
} from '../http/guides/getAll.no-deps'

import {
    type GetReportGuideDetailsBulkEndpoint,
    getReportGuideDetailsBulkRequest,
} from '../http/guides/getReportGuideDetailsBulk.no-deps'

import { getAllUserTraining } from '../http/training/getAll.no-deps'

import {
    type GetOrderEndpoint as GetPostsOrderEndpoint,
    getOrderRequest as getPostsOrderRequest,
} from '../http/posts/getOrder.no-deps'

import { type GetEndpoint as GetPostsEndpoint, getRequest as getPostsRequest } from '../http/posts/get.no-deps'

import type { Id as GuideId, ReportGuideId } from '../../shared/domain/guides/types.no-deps'

import { type EditGuideEndpoint, editGuideRequest } from '../http/guides/editGuide.no-deps'
import { type EditGuidesBulkEndpoint, editGuidesBulkRequest } from '../http/guides/editGuidesBulk.no-deps'
import {
    type BulkDeleteEndpoint,
    bulkDeleteRequest as deleteGuidesBulkRequest,
} from '../http/guides/bulkDelete.no-deps'
import { addSubscriptionRequest, type PushSubscription } from '../http/notifications/addSubscription.no-deps'
import { removeSubscriptionRequest } from '../http/notifications/removeSubscription.no-deps'
import { autocompleteOverdueReportsRequest } from '../http/reports/autocompleteOverdueReports.no-deps'
import { editReportAnswersRequest } from '../http/reports/edit.no-deps'
import {
    type SyncTrainingCompletionsEndpoint,
    syncTrainingCompletionsRequest,
} from '../http/training/syncCompleted.no-deps'
import type { GetAllWithNamesOptions } from '../../shared/domain/units/types.no-deps'
import type { WorkflowFeedbackStatus, WorkflowFeedbackSync } from '../../shared/domain/workflowFeedback/types.no-deps'
import { bulkAddRequest as bulkAddWorkflowFeedbacksRequest } from '../http/workflowFeedbacks/bulkAdd.no-deps'
import {
    getRequest as getWorkflowFeedbacksRequest,
    type GetEndpoint as GetWorkflowFeedbacksEndpoint,
} from '../http/workflowFeedbacks/get.no-deps'
import {
    updateRequest as updateWorkflowFeedbackRequest,
    type UpdateEndpoint as UpdateWorkflowFeedbackEndpoint,
} from '../http/workflowFeedbacks/update.no-deps'
import { getCountsRequest as getWorkflowFeedbackCountRequest } from '../http/workflowFeedbacks/getCounts.no-deps'
import { getUsersRequest as getAllUsersRequest, type GetUsersEndpoint } from '../http/users/getAll.no-deps'
import type { AppNote } from '../../shared/domain/notes/types.no-deps'
import { syncNotesRequest } from '../http/notes/sync.no-deps'
import { invalidateRequest } from '../http/training/invalidate.no-deps'
import { getPromoteReportInitialValuesRequest } from '../http/guides/getPromoteReportInitialValues.no-deps'
import { createManualCertificationRequest } from '../http/reports/createManualCertification.no-deps'
import { type GetAllTeamsEndpoint, getAllRequest as getAllTeamsRequest } from '../http/teams/getAll.no-deps'
import { getReportHistoryRequest } from '../http/reports/getReportHistory.no-deps'
import {
    createBody as createUpsertProfileImageRequestBody,
    upsertProfileImageRequest,
} from '../http/users/updateProfileImage.no-deps'
import { getCommentsRequest } from '../http/posts/getComments.no-deps'
import { addCommentsRequest } from '../http/posts/addComments.no-deps'
import { getGeneratedStepsRequest } from '../http/workflows/getGeneratedSteps.no-deps'
import type { Input as GetReportInput, Output as GetReportOutput } from '../websocket/reports/get.no-deps'
import { proxyRequest } from '../http/websocket/proxy.no-deps'
import { getMetricsRequest, type GetMetricsEndpoint } from '../http/bi/getMetrics.no-deps'
import type { SupportedLanguage } from '../../shared/i18n/types.no-deps'
import { getRequest as getActionsRequest } from '../http/actions/getAll.no-deps'
import { getFlowTranslationRequest } from '../http/flows/getTranslation.no-deps'
import type { CachedUnitData } from '../../shared/domain/units/CachedUnitData.no-deps'
import { inviteRequest, type InviteEndpoint } from '../http/invitations/invite.no-deps'
import { bulkInviteRequest, type BulkInviteEndpoint } from '../http/invitations/bulk.no-deps'
import { addAiChatFileRequest } from '../http/aiChats/addFile.no-deps'
import { getAiChatFilesRequest } from '../http/aiChats/getFiles.no-deps'
import { getCreditBalanceRequest } from '../http/creditBalances/get.no-deps'
import { topUpCreditBalanceRequest } from '../http/creditBalances/topUp.no-deps'
import { getCreditBalancesRequest } from '../http/creditBalances/getAll.no-deps'
import { getTopUpsRequest } from '../http/creditBalances/getTopUps.no-deps'
import type { LoopPath } from '../../shared/domain/flows/LoopPath.no-deps'
import { addMetricDefinitionRequest, type AddMetricDefinitionEndpoint } from '../http/bi/addMetricDefinition.no-deps'
import { getMetricDefinitionRequest } from '../http/bi/getMetricDefinition.no-deps'
import type { MetricDefinitionId } from '../../shared/domain/bi/metrics/types.no-deps'
import { getMetricsForDefinedMetricRequest } from '../http/bi/getMetricsForDefinedMetric.no-deps'
import { listMetricDefinitionsRequest } from '../http/bi/listMetricDefinitions.no-deps'
import { removeMetricDefinitionRequest } from '../http/bi/removeMetricDefinition.no-deps'
import { renameMetricDefinitionRequest } from '../http/bi/renameMetricDefinition.no-deps'
import { searchRequest as globalSearchRequest } from '../http/search/search.no-deps'
import {
    updateMetricDefinitionRequest,
    type UpdateMetricDefinitionEndpoint,
} from '../http/bi/updateMetricDefinition.no-deps'
import { bulkEmbeddingOpsRequest, type Operation } from '../http/embeddings/bulk.no-deps'
import type { WorkflowType } from '../../shared/domain/workflows/types.no-deps'
import type { GlobalSearchOptions } from '../../shared/domain/globalSearch/types.no-deps'
import { type GetUserTrainingEndpoint, getUserTrainingRequest } from '../http/training/getUserTraining.no-deps'

export type {
    AddLogsEndpoint,
    EditActionEndpoint,
    DeleteActionEndpoint,
    CompleteMultipartUploadEndpoint,
    GenerateUploadPartUrlEndpoint,
    GenerateUploadUrlEndpoint,
    StartMultipartUploadEndpoint,
    AddFileMetaEndpoint,
    GetFileMetaEndpoint,
    GetFileEndpoint,
    AwardPointsEndpoint,
    SyncProceduresEndpoint,
    ConfirmActionEndpoint,
    SyncDoneEndpoint,
    AddScheduleEndpoint,
    CreateScheduleEndpoint,
    GetScheduleEndpoint,
    SyncTodoEndpoint,
    GetAllUnitsEndpoint,
    GetAllUnitsWithNamesEndpoint,
    GetManyUnitsWithNamesEndpoint,
    GetAllUnitsWithLineageEndpoint,
    GetUnitsByBulkEditIdEndpoint,
    GetProcedureEndpoint,
    GetAllProceduresEndpoint,
    GetWorkflowsEndpoint,
    GetWorkflowEndpoint,
    SyncWorkflowsEndpoint,
    GetCommmentTimelineEndpoint,
    RemoveCommentEndpoint,
    EditCommentEndpoint,
    RemoveReportEndpoint,
    MarkNotificationsAsReadEndpoint,
    SyncNotificationsEndpoint,
    SyncTeamsEndpoint,
    PingEndpoint,
    AddReportCollaboratorsEndpoint,
    CancelReportEndpoint,
    GetCollaborativeReportsEndpoint,
    GetOngoingReportEndpoint,
    GetOngoingReportChangesEndpoint,
    StartReportEndpoint,
    CreateAnswersEndpoint,
    GetReportHashEndpoint,
    FinalizeReportEndpoint,
    GetReportTitleEndpoint,
    EditWorkflowEndpoint,
    GetUnfinalizedReportsEndpoint,
    GetReportPhotoFilesEndpoint,
    GetUnitsByIdsEndpoint,
    GetAllGuidesEndpoint,
    GetPostsOrderEndpoint,
    GetMetricsEndpoint,
}

interface CommonSyncParams {
    lastSyncTime: Date
    teams: number[]
}

const getBody = <T>(response: { body: T }) => response.body

const mapCommonSyncParams = (params: CommonSyncParams) => ({
    lastSyncTime: params.lastSyncTime.toISOString(),
    teams: params.teams,
})

export type Client = ReturnType<typeof createClient>

export function createClient(httpRequest: HttpRequest) {
    return {
        addLogs(metadata: ClientLogMetadata, logs: ClientLog[], batchId?: UUID) {
            const body = !batchId ? { metadata, logs } : { metadata, logs, batchId }
            return addLogsRequest(httpRequest, { body }).then(getBody)
        },
        deleteGuidesBulk(body: BulkDeleteEndpoint['Body']) {
            return deleteGuidesBulkRequest(httpRequest, { body }).then(getBody)
        },
        ping() {
            return pingRequest(httpRequest, {}).then(getBody)
        },
        deleteAction(id: UUID) {
            return deleteActionRequest(httpRequest, { params: { id } }).then(getBody)
        },
        editAction(id: UUID, action: EditActionEndpoint['Body']) {
            return editActionRequest(httpRequest, { params: { id }, body: action }).then(getBody)
        },
        generateUploadUrl(body: GenerateUploadUrlEndpoint['Body']) {
            return generateUploadUrlRequest(httpRequest, { body }).then(getBody)
        },
        completeMultipartUpload(body: CompleteMultipartUploadEndpoint['Body'], shouldTranscode?: boolean) {
            const query = shouldTranscode ? { shouldTranscode } : {}
            return completeMultipartUploadRequest(httpRequest, { body, query }).then(getBody)
        },
        generateUploadPartUrl(body: GenerateUploadPartUrlEndpoint['Body']) {
            return generateUploadPartUrlRequest(httpRequest, { body }).then(getBody)
        },
        startMultipartUpload(body: StartMultipartUploadEndpoint['Body']) {
            return startMultipartUploadRequest(httpRequest, { body }).then(getBody)
        },
        addFileMeta(body: AddFileMetaEndpoint['Body']) {
            return addFileMetaRequest(httpRequest, { body }).then(getBody)
        },
        getFileMeta(fileId: FileId) {
            const params = { fileId }
            return getFileMetaRequest(httpRequest, { params }).then(getBody)
        },
        getFile(fileId: FileId, query: GetFileEndpoint['Query']) {
            const params = { fileId }
            return getFileRequest(httpRequest, { params, query }).then(getBody)
        },
        getFilesS3Url(fileIds: FileId[], query?: GetFilesS3urlEndpoint['Query']) {
            const body = { fileIds }
            return getFilesS3UrlRequest(httpRequest, { body, query }).then(getBody)
        },
        getAllTrainingForUser(params: GetUserTrainingEndpoint['Params']) {
            return getUserTrainingRequest(httpRequest, { params }).then(getBody)
        },
        getFileMetaBulk(body: GetMetaBulkEndpoint['Body']) {
            return getMetaBulkRequest(httpRequest, { body }).then(getBody)
        },
        awardPoints(pointsAwardedEvents: PointsAwardedEvent[]) {
            const body = { pointsAwardedEvents }
            return awardPointsRequest(httpRequest, { body }).then(getBody)
        },
        createProcedure(procedure: CreateProcedureEndpoint['Body']) {
            return createProcedureRequest(httpRequest, { body: procedure }).then(getBody)
        },
        syncProcedures(params: CommonSyncParams, includeDeleted?: boolean) {
            const body = mapCommonSyncParams(params)
            const query = includeDeleted ? { includeDeleted } : {}

            return syncProceduresRequest(httpRequest, { body, query }).then(getBody)
        },
        confirmAction(body: ConfirmActionEndpoint['Body']) {
            return confirmActionRequest(httpRequest, { body }).then(getBody)
        },
        syncDone(params: CommonSyncParams) {
            const body = mapCommonSyncParams(params)
            return syncDoneRequest(httpRequest, { body }).then(getBody)
        },
        addSchedule(schedule: AddScheduleEndpoint['Body']) {
            return addScheduleRequest(httpRequest, { body: schedule }).then(getBody)
        },
        createSchedule(schedule: CreateScheduleEndpoint['Body']) {
            return createScheduleRequest(httpRequest, { body: schedule }).then(getBody)
        },
        getSchedule(procedure: number, workflow: number) {
            return getScheduleRequest(httpRequest, { query: { procedure, workflow } }).then(getBody)
        },
        syncTodo(params: CommonSyncParams) {
            const body = {
                ...mapCommonSyncParams(params),
                sendWorkflowActions: true as true,
            }
            return syncTodoRequest(httpRequest, { body }).then(getBody)
        },
        getUnitsByIds(unitIds: number[], withNames = false) {
            return getUnitsByIdsRequest(httpRequest, { body: { unitIds, withNames } }).then(getBody)
        },
        searchUnits(q: string) {
            return searchUnitsRequest(httpRequest, { query: { q } }).then((r) => r.body.units)
        },
        updateUnits(units: UpdateUnitsEndpoint['Body']['units']) {
            return updateUnitsRequest(httpRequest, { body: { units } })
        },
        syncUnits(lastSyncTime?: Date, filterExpired?: boolean) {
            const query = filterExpired ? { lastSyncTime, filterExpired } : { lastSyncTime }
            return getAllUnitsRequest(httpRequest, { query }).then(getBody)
        },

        getAllUnits(query: GetAllUnitsEndpoint['Query']) {
            return getAllUnitsRequest(httpRequest, { query }).then(getBody)
        },

        getAllUnitsWithNames(query: GetAllWithNamesOptions) {
            return getAllUnitsWithNamesRequest(httpRequest, { query }).then(getBody)
        },

        getManyUnitsWithNames(body: { unitIds?: number[] }, query: Omit<GetAllWithNamesOptions, 'unitIds'>) {
            return getManyUnitsWithNamesRequest(httpRequest, { body, query }).then(getBody)
        },

        getAllUnitsWithLineage() {
            return getAllUnitsWithLineageRequest(httpRequest, {}).then(getBody)
        },

        getUnitsByBulkEditId(bulkEditId: UUID) {
            return getUnitsByBulkEditIdRequest(httpRequest, { query: { bulkEditId } }).then(getBody)
        },

        getUnits(query: GetUnitsEndpoint['Query']) {
            return getUnitsRequest(httpRequest, { query }).then(getBody)
        },

        getAllUnitTypesWithNames(unitTypeIds?: number[]) {
            return getAllWithNamesRequest(httpRequest, { body: { unitTypeIds } }).then(getBody)
        },
        syncFlowWorkflows(
            params: CommonSyncParams,
            options?: { includeUnitSubjectType?: boolean; richText?: boolean; nullDeletedFlows?: boolean }
        ) {
            const query: { includeUnitSubjectType?: boolean; richText?: boolean; nullDeletedFlows?: boolean } = {}

            query.includeUnitSubjectType = options?.includeUnitSubjectType ?? true

            if (options?.richText) {
                query.richText = options.richText
            }
            if (options?.nullDeletedFlows) {
                query.nullDeletedFlows = options.nullDeletedFlows
            }

            return syncFlowWorkflowsRequest(httpRequest, {
                body: {
                    ...params,
                    /** @deprecated Can be removed after v75.0 released */
                    sendAll: true,
                },
                query,
            }).then(getBody)
        },
        bulkAddWorkflowFeedbacks(workflowFeedbacks: WorkflowFeedbackSync[]) {
            return bulkAddWorkflowFeedbacksRequest(httpRequest, { body: { workflowFeedbacks } })
        },
        getWorkflowFeedbacks(workflowId: number, query: GetWorkflowFeedbacksEndpoint['Query']) {
            return getWorkflowFeedbacksRequest(httpRequest, {
                params: { workflowId },
                query,
            }).then((res) => res.body.workflowFeedbacks)
        },
        getWorkflowFeedbackCount(workflowId: number, status?: WorkflowFeedbackStatus) {
            return getWorkflowFeedbackCountRequest(httpRequest, { params: { workflowId }, query: { status } }).then(
                (res) => res.body.counts
            )
        },
        updateWorkflowFeedback(feedbackId: UUID, body: UpdateWorkflowFeedbackEndpoint['Body']) {
            return updateWorkflowFeedbackRequest(httpRequest, { params: { feedbackId }, body }).then(getBody)
        },
        getGeneratedSteps(workflow: number, prompt: string) {
            return getGeneratedStepsRequest(httpRequest, { params: { id: workflow }, body: { prompt } }).then(getBody)
        },
        getPostComments({
            postId,
            id,
            richText,
            language,
        }: {
            postId: UUID
            id?: UUID | UUID[]
            richText?: boolean
            language?: string
        }) {
            const params = { id: postId }
            const query = { id, richText, language }

            return getCommentsRequest(httpRequest, { params, query }).then(getBody)
        },
        getCommentTimeline({
            postId,
            version,
            richText,
            id,
            includeLanguage,
        }: {
            postId: UUID
            version: number
            richText?: boolean
            id?: UUID | UUID[]
            includeLanguage?: boolean
        }) {
            const params = { id: postId }

            const query = version > 1 ? { richText, version, id, includeLanguage } : { richText, id, includeLanguage }

            return getCommentTimelineRequest(httpRequest, { params, query }).then(getBody)
        },
        addComments(
            id: UUID,
            text: RichTextParagraph[],
            {
                includeLanguage,
                workflowElement,
                loopPath,
            }: {
                includeLanguage?: boolean
                workflowElement?: UUID
                loopPath?: LoopPath
            }
        ) {
            return addCommentsRequest(httpRequest, {
                params: { id },
                body: { text, includeLanguage, workflowElement, loopPath },
            }).then(getBody)
        },
        getProcedure(procedureId: number) {
            const params = { id: procedureId }
            return getProcedureRequest(httpRequest, { params }).then(getBody)
        },
        getProcedures(ids?: number | number[]) {
            return getAllProceduresRequest(httpRequest, {
                query: {
                    ...(ids && [].concat(ids).length > 0 && { id: ids }),
                },
            }).then(getBody)
        },
        getWorkflows(hasFlow: boolean = false) {
            return getWorkflowsRequest(httpRequest, { query: { hasFlow } }).then(getBody)
        },
        getWorkflow(
            workflowId: number,
            options?: {
                workflowVersion?: number
                includeDeleted?: boolean
                richText?: boolean
                language?: SupportedLanguage
            }
        ) {
            const params = { id: workflowId }
            const query: {
                includeDeleted?: boolean
                workflowVersion?: number
                richText?: boolean
                language?: SupportedLanguage
            } = {
                includeDeleted: options?.includeDeleted,
            }

            if (options?.workflowVersion) {
                query.workflowVersion = options.workflowVersion
            }
            if (options?.richText) {
                query.richText = options.richText
            }
            if (options?.language) {
                query.language = options.language
            }
            return getWorkflowRequest(httpRequest, { params, query }).then(getBody)
        },
        addWorkflow(procedureId: number, workflow: AddWorkflowEndpoint['Body']) {
            return addWorkflowRequest(httpRequest, { body: workflow, params: { procedure: procedureId } }).then(getBody)
        },
        removeComment(commentId: UUID) {
            const params = { id: commentId }
            return removeCommentRequest(httpRequest, { params }).then(getBody)
        },
        editComment(commentId: UUID, text: RichTextParagraph[], includeLanguage?: boolean) {
            const params = { id: commentId }
            const body = { text, includeLanguage }
            return editCommentRequest(httpRequest, { params, body }).then(getBody)
        },
        removeReport(reportId: number) {
            const params = { id: reportId }
            return removeReportRequest(httpRequest, { params }).then(getBody)
        },
        markNotificationsAsRead(
            postComments: UUID[],
            postEvents: UUID[],
            scheduledInstances: UUID[],
            notificationIds?: UUID[]
        ) {
            const body = { postComments, postEvents, scheduledInstances, notificationIds }
            return markNotificationsAsReadRequest(httpRequest, { body }).then(getBody)
        },
        syncNotifications(lastSyncTime: Date, version: number) {
            const body: SyncNotificationsEndpoint['Body'] = {
                notifications: [], // This argument should be removed in #3950
                lastSyncTime,
                includeScheduledInstanceNotifications: true,
            }

            const query = version > 1 ? { version } : {}

            return syncNotificationsRequest(httpRequest, { body, query }).then(getBody)
        },
        syncTeams(query: SyncTeamsEndpoint['Query']) {
            return syncTeamsRequest(httpRequest, { query }).then(getBody)
        },
        syncReportStatuses(lastSyncTime?: Date) {
            const query = { lastSyncTime }
            return syncReportStatusesRequest(httpRequest, { query }).then(getBody)
        },
        syncReportStatusFlows(lastSyncTime?: Date) {
            const query = { lastSyncTime }
            return syncReportStatusFlowsRequest(httpRequest, { query }).then(getBody)
        },
        getUserReports(user: number, procedure: number, workflow: number, unit?: number) {
            const query = {
                user,
                procedure,
                workflows: [workflow],
                workflowType: 'training' as WorkflowType,
                ...(unit ? { units: [unit] } : {}),
            }
            return getUserReports(httpRequest, { query }).then(getBody)
        },
        addReportCollaborators(reportClientId: ReportClientId, collaborators: number[]) {
            const body = { reportClientId, collaborators }
            return addReportCollaboratorsRequest(httpRequest, { body }).then(getBody)
        },
        cancelReport(reportClientId: ReportClientId) {
            const params = { reportClientId }
            return cancelReportRequest(httpRequest, { params }).then(getBody)
        },
        getCollaborativeReports() {
            return getCollaborativeReportsRequest(httpRequest, {}).then(getBody)
        },
        getOngoingReport(reportClientId: ReportClientId) {
            const query = { reportClientId }
            return getOngoingReportRequest(httpRequest, { query }).then(getBody)
        },
        getOngoingReportChanges(reportClientId: ReportClientId, lastSyncTime: Date) {
            const params = { reportClientId }
            const query = { lastSyncTime }
            return getOngoingReportChangesRequest(httpRequest, { params, query }).then(getBody)
        },
        startReport(ongoingReport: OngoingReportIn) {
            const body = { ongoingReport }
            return startReportRequest(httpRequest, { body }).then(getBody)
        },
        getReportHash(reportClientId: ReportClientId, statusId?: number) {
            const params = { reportClientId }
            const query = { ...(statusId && { statusId }) }
            return getReportHashReqeust(httpRequest, { params, query }).then(getBody)
        },
        createAnswers(reportClientId: ReportClientId, answers: FlowAnswer[]) {
            const body = { clientId: reportClientId, answers }
            return createAnswersRequest(httpRequest, { body }).then(getBody)
        },
        editAnswers(reportClientId: ReportClientId, answers: FlowAnswer[]) {
            const body = { clientId: reportClientId, answers }
            return editReportAnswersRequest(httpRequest, { body }).then(getBody)
        },
        markReportAsConfidential(reportClientId: ReportClientId, isConfidential: boolean) {
            const body = { clientId: reportClientId, isConfidential }
            return editReportAnswersRequest(httpRequest, { body }).then(getBody)
        },
        finalizeReport(
            reportClientId: ReportClientId,
            cachedUnitData: CachedUnitData,
            {
                doSendNotifications,
                signature,
                signedAtTime,
                editAlreadyCompletedReport,
            }: {
                doSendNotifications?: boolean
                signature?: string
                signedAtTime?: Date
                editAlreadyCompletedReport?: boolean
            } = {}
        ) {
            const params = { reportClientId }

            return finalizeReportRequest(httpRequest, {
                params,
                query: {
                    doSendNotifications,
                    signature,
                    signedAtTime,
                    editAlreadyCompletedReport,
                },
                body: { cachedUnitData },
            }).then(getBody)
        },
        getReport(input: GetReportInput) {
            return proxyRequest(httpRequest, { params: { method: 'v2.reports.get' }, body: input }).then(
                getBody
            ) as Promise<GetReportOutput>
        },
        getReportTitle(reportId: number) {
            const params = { id: reportId }
            return getReportTitleRequest(httpRequest, { params }).then(getBody)
        },
        getReportHistory(reportId: number, opts?: { includeRequestedReport?: boolean; includeLanguage?: boolean }) {
            const params = { id: reportId }
            const query = {
                includeRequestedReport: opts?.includeRequestedReport || false,
                includeLanguage: opts?.includeLanguage,
            }
            return getReportHistoryRequest(httpRequest, { params, query }).then(getBody)
        },
        editWorkflow(procedure: number, workflow: number, body: EditWorkflowEndpoint['Body']) {
            const params = { procedure, workflow }
            const query = { richText: true }
            return editWorkflowRequest(httpRequest, { params, body, query }).then(getBody)
        },
        getUnfinalizedReports() {
            return getUnfinalizedReportsRequest(httpRequest, {}).then(getBody)
        },
        getReportPhotoFiles(reportClientIds: UUID[] | UUID) {
            return getReportPhotoFilesRequest(httpRequest, { query: { reportClientIds } }).then(getBody)
        },
        getUnitReports(
            unitId: number,
            offset?: number,
            limit?: number,
            submittedBefore?: Date,
            statusIds?: number[],
            workflowIds?: number[],
            includeDescendants?: boolean
        ) {
            const params = { unitId }
            const query = { offset, limit, submittedBefore, statusIds, workflowIds, includeDescendants }
            return getReportsRequest(httpRequest, { params, query }).then(getBody)
        },
        getUnitReportFilters(unitId: number, { includeDescendants = true } = {}) {
            const params = { unitId }
            const query = { includeDescendants }
            return getUnitReportFiltersRequest(httpRequest, { params, query }).then(getBody)
        },
        getAllTags() {
            return getTags(httpRequest, {}).then(getBody)
        },
        getAllGuides({
            lastSyncTime,
            guideIds,
            unitIds,
            procedureIds,
            tagIds,
            searchQuery,
            includeDeleted,
            includeFileThumbnailUrls,
        }: {
            lastSyncTime?: Date
            guideIds?: GuideId[]
            unitIds?: number[]
            procedureIds?: number[]
            tagIds?: number[]
            searchQuery?: string
            includeDeleted?: boolean
            includeFileThumbnailUrls?: boolean
        } = {}) {
            const query = { lastSyncTime }
            const body: GetAllGuidesEndpoint['Body'] = {
                ...(guideIds?.length > 0 && { guideIds }),
                ...(unitIds?.length > 0 && { unitIds }),
                ...(procedureIds?.length > 0 && { procedureIds }),
                ...(tagIds?.length > 0 && { tagIds }),
                ...(searchQuery && { query: searchQuery }),
                includeDeleted: !!includeDeleted,
                includeFileThumbnailUrls: !!includeFileThumbnailUrls,
            }

            return getAllGuidesRequest(httpRequest, { query, body }).then(getBody)
        },
        getReportGuideDetailsBulk({ ids, lastSyncTime }: { ids: ReportGuideId[]; lastSyncTime?: Date }) {
            const body: GetReportGuideDetailsBulkEndpoint['Body'] = { ids }
            const query: GetReportGuideDetailsBulkEndpoint['Query'] = { lastSyncTime }
            return getReportGuideDetailsBulkRequest(httpRequest, { body, query }).then(getBody)
        },
        getPromoteReportInitialValues(reportId: number) {
            return getPromoteReportInitialValuesRequest(httpRequest, { query: { reportId } }).then(getBody)
        },
        getUserTraining(procedureId: number) {
            const query = { procedureId }
            return getAllUserTraining(httpRequest, { query }).then(getBody)
        },
        invalidateUserTraining(trainingId: number, userId: number) {
            return invalidateRequest(httpRequest, { params: { trainingId, userId }, query: {} })
        },
        createManualCertificationRequest(workflowId: number, userId: number) {
            return createManualCertificationRequest(httpRequest, { params: { userId, workflowId }, body: {} })
        },
        addSubscription(subscription: PushSubscription) {
            const body = { subscription }
            return addSubscriptionRequest(httpRequest, { body }).then(getBody)
        },
        removeSubscription(endpoint: string) {
            const body = { endpoint }
            return removeSubscriptionRequest(httpRequest, { body }).then(getBody)
        },
        autocompleteOverdueReports() {
            return autocompleteOverdueReportsRequest(httpRequest, {}).then(getBody)
        },
        getPostsOrder(query: GetPostsOrderEndpoint['Query']) {
            return getPostsOrderRequest(httpRequest, { query })
        },
        getPosts(query: GetPostsEndpoint['Query']) {
            return getPostsRequest(httpRequest, { query })
        },
        editGuide(params: EditGuideEndpoint['Params'], body: EditGuideEndpoint['Body']) {
            return editGuideRequest(httpRequest, { params, body }).then(getBody)
        },
        editGuidesBulk(body: EditGuidesBulkEndpoint['Body']) {
            return editGuidesBulkRequest(httpRequest, { body }).then(getBody)
        },
        getAllUsers(query: GetUsersEndpoint['Query'] = {}) {
            return getAllUsersRequest(httpRequest, { query })
                .then(getBody)
                .then((b) => b.users)
        },
        updateUserProfileImage(newImage: Blob | null) {
            const body = createUpsertProfileImageRequestBody(newImage)
            return upsertProfileImageRequest(httpRequest, { body }).then(getBody)
        },
        createAiChat() {
            return createAiChatRequest(httpRequest, {}).then(getBody)
        },
        updateAiPromptFeedback(chatId: ChatId, promptId: ChatPromptId, body: UpdateAiPromptFeedbackEndpoint['Body']) {
            const params = { chatId, promptId }

            return updateAiPromptFeedbackRequest(httpRequest, { params, body })
        },
        syncNotes(notes: AppNote[], lastSyncTime: Date) {
            return syncNotesRequest(httpRequest, { body: { notes, lastSyncTime } }).then(getBody)
        },
        syncTrainingCompletions(query: SyncTrainingCompletionsEndpoint['Query']) {
            return syncTrainingCompletionsRequest(httpRequest, { query }).then(getBody)
        },
        getTeams(query: GetAllTeamsEndpoint['Query']) {
            return getAllTeamsRequest(httpRequest, { query }).then(getBody)
        },
        getActions(scheduleInstance: UUID, language?: SupportedLanguage) {
            return getActionsRequest(httpRequest, { query: { scheduleInstance, language } }).then(getBody)
        },
        getFlowTranslation(flow: Flow, flowName: string, language: SupportedLanguage) {
            return getFlowTranslationRequest(httpRequest, { body: { flow, name: flowName, language } }).then(getBody)
        },
        invite(body: InviteEndpoint['Body']) {
            return inviteRequest(httpRequest, { body }).then(getBody)
        },
        inviteBulk(body: BulkInviteEndpoint['Body']) {
            return bulkInviteRequest(httpRequest, { body }).then(getBody)
        },
        addAiChatFile(chatId: ChatId, file: FileId) {
            return addAiChatFileRequest(httpRequest, { params: { chatId }, body: { file } }).then(getBody)
        },
        getAiChatFiles(chatId: ChatId, files: FileId[]) {
            return getAiChatFilesRequest(httpRequest, { params: { chatId }, query: { files } }).then(getBody)
        },
        addMetricDefinition(body: AddMetricDefinitionEndpoint['Body']) {
            return addMetricDefinitionRequest(httpRequest, { body }).then((r) => r.body.definitionId)
        },
        getMetricDefinition(definitionId: MetricDefinitionId) {
            return getMetricDefinitionRequest(httpRequest, { params: { definitionId } }).then(getBody)
        },
        getMetrics(body: GetMetricsEndpoint['Body']) {
            return getMetricsRequest(httpRequest, { body }).then((r) => r.body.metrics)
        },
        getMetricsForDefinedMetric(definitionId: MetricDefinitionId) {
            return getMetricsForDefinedMetricRequest(httpRequest, { params: { definitionId } }).then(
                (r) => r.body.metrics
            )
        },
        listMetricDefinitions() {
            return listMetricDefinitionsRequest(httpRequest, {}).then((r) => r.body.definitions)
        },
        removeMetricDefinition(definitionId: MetricDefinitionId) {
            return removeMetricDefinitionRequest(httpRequest, { params: { definitionId } })
        },
        renameMetricDefinition(definitionId: MetricDefinitionId, name: string) {
            return renameMetricDefinitionRequest(httpRequest, { params: { definitionId }, body: { name } })
        },
        updateMetricDefinition(definitionId: MetricDefinitionId, body: UpdateMetricDefinitionEndpoint['Body']) {
            return updateMetricDefinitionRequest(httpRequest, { params: { definitionId }, body })
        },
        getCreditBalance(slug: string) {
            return getCreditBalanceRequest(httpRequest, { params: { slug } }).then(getBody)
        },
        getCreditBalances() {
            return getCreditBalancesRequest(httpRequest, {}).then(getBody)
        },
        getCreditBalanceTopUps(creditBalanceSlug: string) {
            return getTopUpsRequest(httpRequest, { params: { slug: creditBalanceSlug } }).then(getBody)
        },
        topUpCreditBalance(slug: string, amount: number, monetarySum: Nullable<number>, note: Nullable<string>) {
            return topUpCreditBalanceRequest(httpRequest, {
                params: { slug },
                body: { amount, monetarySum, note },
            }).then(getBody)
        },
        bulkEmbeddingOperations(operations: Operation[]) {
            return bulkEmbeddingOpsRequest(httpRequest, { body: { operations } }).then(getBody)
        },
        getGlobalSearchResults(
            query: string,
            opts?: { itemsFilters?: GlobalSearchOptions['itemsFilters']; sortBy?: GlobalSearchOptions['sortBy'] }
        ) {
            return globalSearchRequest(httpRequest, {
                body: { q: query, itemsFilters: opts.itemsFilters, sortBy: opts.sortBy },
            }).then(getBody)
        },
    }
}
